import * as React from "react";
import {
  Title,
  useDataProvider,
  useNotify,
  useRedirect,
  useGetIdentity,
} from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { useClub } from "../club/ClubContext";

type TargetMode = "ALL" | "TEAMS" | "USERS";
type Severity = "info" | "warn" | "critical";



type Team = { id: number; name: string; display_name?: string };
type User = { id: number; display_name?: string; email?: string };

export default function MessageBroadcastPage() {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const { identity, isLoading: identityLoading } = useGetIdentity();
  const { clubId } = useClub();

  const [targetMode, setTargetMode] = React.useState<TargetMode>("ALL");
  const [teamIds, setTeamIds] = React.useState<number[]>([]);
  const [userIds, setUserIds] = React.useState<number[]>([]);

  const [channelsInApp, setChannelsInApp] = React.useState(true);
  const [channelsEmail, setChannelsEmail] = React.useState(false);
  const [channelsPush, setChannelsPush] = React.useState(false);

  const [severity, setSeverity] = React.useState<Severity>("info");
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [linkUrl, setLinkUrl] = React.useState("");

  const [teams, setTeams] = React.useState<Team[]>([]);
  const [users, setUsers] = React.useState<User[]>([]);
  const [loadingLists, setLoadingLists] = React.useState(false);

  const canSend = Boolean(clubId) && title.trim().length > 0 && body.trim().length > 0;

  const loadTeamsAndUsers = React.useCallback(async () => {
    if (!clubId) return;
    setLoadingLists(true);
    try {
      // Teams
      const tRes = await dataProvider.getList("teams", {
        pagination: { page: 1, perPage: 500 },
        sort: { field: "name", order: "ASC" },
        filter: { clubId }, // falls euer backend clubId oder club_id erwartet, passt du hier an
      } as any);
      setTeams((tRes?.data ?? []) as Team[]);

      // Users (für MultiSelect; ggf. später mit Suche/Autocomplete)
      const uRes = await dataProvider.getList("users", {
        pagination: { page: 1, perPage: 500 },
        sort: { field: "id", order: "ASC" },
        filter: { clubId }, // dito
      } as any);
      setUsers((uRes?.data ?? []) as User[]);
    } catch (e: any) {
      notify(e?.message ?? "Konnte Teams/Users nicht laden", { type: "warning" });
    } finally {
      setLoadingLists(false);
    }
  }, [clubId, dataProvider, notify]);

  React.useEffect(() => {
    // nur laden wenn nötig
    if (clubId && (targetMode === "TEAMS" || targetMode === "USERS")) {
      loadTeamsAndUsers();
    }
  }, [clubId, targetMode, loadTeamsAndUsers]);

  const onSubmit = async () => {
    if (!clubId) {
      notify("Bitte zuerst einen Verein auswählen (clubId fehlt).", { type: "warning" });
      return;
    }
    if (!title.trim() || !body.trim()) {
      notify("Titel und Text sind Pflicht.", { type: "warning" });
      return;
    }
    if (!channelsInApp && !channelsEmail && !channelsPush) {
      notify("Mindestens einen Kanal auswählen (In-App, Email oder Push).", { type: "warning" });
      return;
    }
    if (targetMode === "TEAMS" && teamIds.length === 0) {
      notify("Bitte mindestens ein Team auswählen.", { type: "warning" });
      return;
    }
    if (targetMode === "USERS" && userIds.length === 0) {
      notify("Bitte mindestens einen User auswählen.", { type: "warning" });
      return;
    }

    const payload: any = {
      club_id: clubId,
      target: {
        mode: targetMode,
        team_ids: targetMode === "TEAMS" ? teamIds : undefined,
        user_ids: targetMode === "USERS" ? userIds : undefined,
      },
      channels: {
        inapp: channelsInApp,
        email: channelsEmail,
        push: channelsPush,
      },
      type: "ADMIN_BROADCAST",
      severity,
      title: title.trim(),
      body: body.trim(),
      link_url: linkUrl.trim() ? linkUrl.trim() : null,
    };

    try {
      const res = await dataProvider.create("messages/broadcast", { data: payload } as any);
      const recipientCount = res?.data?.recipient_count ?? res?.data?.recipientCount;
      notify(
        `Nachricht gesendet${recipientCount != null ? ` (${recipientCount} Empfänger)` : ""}.`,
        { type: "info" }
      );
      // optional: zurück zur Inbox oder auf Seite bleiben
      redirect("/inbox");
    } catch (e: any) {
      notify(e?.message ?? "Senden fehlgeschlagen", { type: "warning" });
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Title title="Admin Broadcast" />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack spacing={0.5}>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            Broadcast
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Nachricht an alle, Teams oder bestimmte User senden.
          </Typography>
        </Stack>

        {(identityLoading || loadingLists) && <CircularProgress size={22} />}
      </Stack>

      {!clubId ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Es wurde keine <b>clubId</b> gefunden (LocalStorage). Bitte wähle zuerst einen Verein
          (oder sag mir, wo ihr die clubId im Frontend speichert, dann binde ich es sauber an).
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Club ID: <b>{clubId}</b>
          {identity?.fullName ? (
            <>
              {" "}
              · als <b>{identity.fullName}</b>
            </>
          ) : null}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack spacing={2}>
            {/* Zielgruppe */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="stretch">
              <FormControl fullWidth>
                <InputLabel id="target-mode-label">Zielgruppe</InputLabel>
                <Select
                  labelId="target-mode-label"
                  value={targetMode}
                  label="Zielgruppe"
                  onChange={(e) => {
                    const v = e.target.value as TargetMode;
                    setTargetMode(v);
                    setTeamIds([]);
                    setUserIds([]);
                  }}
                >
                  <MenuItem value="ALL">Alle im Club</MenuItem>
                  <MenuItem value="TEAMS">Bestimmte Teams</MenuItem>
                  <MenuItem value="USERS">Bestimmte User</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="severity-label">Severity</InputLabel>
                <Select
                  labelId="severity-label"
                  value={severity}
                  label="Severity"
                  onChange={(e) => setSeverity(e.target.value as Severity)}
                >
                  <MenuItem value="info">info</MenuItem>
                  <MenuItem value="warn">warn</MenuItem>
                  <MenuItem value="critical">critical</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {targetMode === "TEAMS" ? (
              <FormControl fullWidth>
                <InputLabel id="teams-label">Teams</InputLabel>
                <Select
                  labelId="teams-label"
                  multiple
                  value={teamIds}
                  input={<OutlinedInput label="Teams" />}
                  onChange={(e) => setTeamIds(e.target.value as number[])}
                  renderValue={(selected) => (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                      {(selected as number[]).map((id) => {
                        const t = teams.find((x) => x.id === id);
                        return (
                          <Chip key={id} size="small" label={t?.display_name ?? t?.name ?? `#${id}`} />
                        );
                      })}
                    </Stack>
                  )}
                >
                  {teams.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.display_name ?? t.name ?? `Team #${t.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}

            {targetMode === "USERS" ? (
              <FormControl fullWidth>
                <InputLabel id="users-label">User</InputLabel>
                <Select
                  labelId="users-label"
                  multiple
                  value={userIds}
                  input={<OutlinedInput label="User" />}
                  onChange={(e) => setUserIds(e.target.value as number[])}
                  renderValue={(selected) => (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                      {(selected as number[]).map((id) => {
                        const u = users.find((x) => x.id === id);
                        const label = u?.display_name
                          ? u.display_name
                          : u?.email
                          ? u.email
                          : `#${id}`;
                        return <Chip key={id} size="small" label={label} />;
                      })}
                    </Stack>
                  )}
                >
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.display_name ?? u.email ?? `User #${u.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}

            <Divider />

            {/* Kanäle */}
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Kanäle
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox checked={channelsInApp} onChange={(e) => setChannelsInApp(e.target.checked)} />
                }
                label="In-App"
              />
              <FormControlLabel
                control={
                  <Checkbox checked={channelsEmail} onChange={(e) => setChannelsEmail(e.target.checked)} />
                }
                label="Email"
              />
              <FormControlLabel
                control={
                  <Checkbox checked={channelsPush} onChange={(e) => setChannelsPush(e.target.checked)} />
                }
                label="Push"
              />
            </Stack>

            <Divider />

            {/* Inhalt */}
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Inhalt
            </Typography>

            <TextField
              label="Titel"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              inputProps={{ maxLength: 200 }}
            />

            <TextField
              label="Text"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fullWidth
              required
              multiline
              minRows={5}
            />

            <TextField
              label="Link (optional)"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              fullWidth
              placeholder="https://…"
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => redirect("/inbox")}>
                Abbrechen
              </Button>
              <Button variant="contained" disabled={!canSend} onClick={onSubmit}>
                Senden
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
